import type { SettingsRegistryEntry } from "@magic-md/engine-client/config";
import { useT } from "@magic-md/i18n/react";
import { FolderOpen } from "lucide-react";
import { type KeyboardEvent, useEffect, useRef, useState } from "react";
import {
    PREVIEW_WIDTH_PRESETS,
    normalizePreviewWidth,
} from "../../../lib/preview-width";
import { cn } from "../../../lib/utils";
import { showFolderPickerDialog } from "../../dialogs";
import { RawConfigEditor } from "../../shared";
import {
    formatRawJsonValue,
    formatValue,
    getEnumOptionLabel,
    isPlainObject,
    isThemeSelectorEntryKey,
    parseStringList,
    summarizeListValue,
} from "./format-utils";
import { MiniBadge } from "./LayoutComponents";
import { useSettingsTextResolver } from "./localization";
import {
    directoryLikeKey,
    getSchemaProperties,
    hasSchemaProperties,
    isDirectoryArraySetting,
    isDirectoryStringSetting,
    removeObjectField,
    schemaItemsPrimaryType,
    schemaPrimaryType,
    schemaTypeLabel,
    updateObjectField,
} from "./schema-helpers";
import { ThemeSelectorCardEditor } from "./ThemeSelectors";

export function SettingEditor({
    entry,
    value,
    disabled,
    onChange,
    onCommit,
    onDiscard,
}: {
    entry: SettingsRegistryEntry;
    value: unknown;
    disabled: boolean;
    onChange: (value: unknown) => void;
    onCommit: (value: unknown) => Promise<void>;
    onDiscard: () => void;
}) {
    const t = useT("editor");
    const resolveText = useSettingsTextResolver();
    const rawType = Array.isArray(entry.schema.type)
        ? entry.schema.type[0]
        : entry.schema.type;

    if (rawType === "boolean") {
        return (
            <label className="flex items-center gap-3 text-sm">
                <input
                    type="checkbox"
                    checked={value === true}
                    disabled={disabled}
                    onChange={(event) => {
                        onChange(event.target.checked);
                        void onCommit(event.target.checked);
                    }}
                />
                <span>
                    {value === true
                        ? t("settings.boolean.enabled")
                        : t("settings.boolean.disabled")}
                </span>
            </label>
        );
    }

    if (entry.key === "editor.defaultPreviewWidth" && rawType === "string") {
        const selectedValue =
            typeof value === "string" ? normalizePreviewWidth(value) : "fill";
        return (
            <select
                value={selectedValue.startsWith("custom:") ? "fill" : selectedValue}
                disabled={disabled}
                onChange={(event) => {
                    onChange(event.target.value);
                    void onCommit(event.target.value);
                }}
                className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none transition-colors focus:border-primary"
            >
                {PREVIEW_WIDTH_PRESETS.map((item) => (
                    <option key={item.id} value={item.id}>
                        {getEnumOptionLabel(entry.key, item.id, resolveText)}
                    </option>
                ))}
            </select>
        );
    }

    if (
        rawType === "string" &&
        Array.isArray(entry.schema.enum) &&
        entry.schema.enum.every((item) => typeof item === "string")
    ) {
        if (isThemeSelectorEntryKey(entry.key)) {
            return (
                <ThemeSelectorCardEditor
                    entry={entry}
                    value={typeof value === "string" ? value : undefined}
                    disabled={disabled}
                    onChange={onChange}
                    onCommit={onCommit}
                />
            );
        }

        return (
            <select
                value={typeof value === "string" ? value : ""}
                disabled={disabled}
                onChange={(event) => {
                    onChange(event.target.value);
                    void onCommit(event.target.value);
                }}
                className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none transition-colors focus:border-primary"
            >
                {(entry.schema.enum as string[]).map((item) => (
                    <option key={item} value={item}>
                        {getEnumOptionLabel(entry.key, item, resolveText)}
                    </option>
                ))}
            </select>
        );
    }

    if (rawType === "string" || rawType === "number" || rawType === "integer") {
        const inputType =
            rawType === "number" || rawType === "integer" ? "number" : "text";
        if (inputType === "text" && isDirectoryStringSetting(entry)) {
            return (
                <DirectoryCommitInput
                    value={typeof value === "string" ? value : ""}
                    disabled={disabled}
                    onChange={onChange}
                    onCommit={onCommit}
                    onDiscard={onDiscard}
                />
            );
        }
        return (
            <CommitInput
                type={inputType}
                value={
                    typeof value === "string" || typeof value === "number"
                        ? String(value)
                        : ""
                }
                disabled={disabled}
                onChange={onChange}
                onCommit={onCommit}
                onDiscard={onDiscard}
            />
        );
    }

    if (rawType === "array") {
        if (
            entry.key === "engine.cloudPluginSources" ||
            entry.key === "engine.cloudExtensionSources" ||
            entry.key === "engine.cloudSidecarSources"
        ) {
            return (
                <CloudSourcesEditor
                    value={Array.isArray(value) ? value : []}
                    disabled={disabled}
                    onChange={onChange}
                />
            );
        }
        if (isDirectoryArraySetting(entry)) {
            return (
                <DirectoryListEditor
                    value={Array.isArray(value) ? value : []}
                    disabled={disabled}
                    onChange={onChange}
                />
            );
        }
        if (schemaItemsPrimaryType(entry.schema) !== "string") {
            return (
                <RawJsonDraftEditor
                    value={Array.isArray(value) ? value : []}
                    disabled={disabled}
                    onChange={onChange}
                />
            );
        }
        return (
            <textarea
                value={Array.isArray(value) ? value.join("\n") : ""}
                disabled={disabled}
                onChange={(event) =>
                    onChange(
                        event.target.value
                            .split(/[\n,]/)
                            .map((item) => item.trim())
                            .filter(Boolean),
                    )
                }
                className="min-h-24 w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none transition-colors focus:border-primary"
            />
        );
    }

    if (rawType === "object") {
        if (hasSchemaProperties(entry.schema)) {
            return (
                <ObjectSettingEditor
                    entry={entry}
                    value={isPlainObject(value) ? value : {}}
                    disabled={disabled}
                    onChange={onChange}
                />
            );
        }
        return (
            <RawConfigEditor
                config={isPlainObject(value) ? value : {}}
                onChange={onChange}
                textareaClassName="h-32"
            />
        );
    }

    return (
        <CommitInput
            type="text"
            value={typeof value === "string" ? value : formatValue(value, resolveText)}
            disabled={disabled}
            onChange={onChange}
            onCommit={onCommit}
            onDiscard={onDiscard}
        />
    );
}

export function ObjectSettingEditor({
    entry,
    value,
    disabled,
    onChange,
}: {
    entry: SettingsRegistryEntry;
    value: Record<string, unknown>;
    disabled: boolean;
    onChange: (value: unknown) => void;
}) {
    const properties = getSchemaProperties(entry.schema);

    if (properties.length === 0) {
        return (
            <RawConfigEditor
                config={value}
                onChange={onChange}
                textareaClassName="h-32"
            />
        );
    }

    return (
        <div className="overflow-hidden rounded-md border border-border/80 bg-muted/10">
            {properties.map(([fieldName, fieldSchema], index) => {
                const hasValue = Object.hasOwn(value, fieldName);
                const fieldValue = hasValue ? value[fieldName] : undefined;

                return (
                    <ObjectSettingField
                        key={fieldName}
                        fieldName={fieldName}
                        fieldPath={`${entry.key}.${fieldName}`}
                        schema={fieldSchema}
                        value={fieldValue}
                        hasValue={hasValue}
                        disabled={disabled}
                        onChange={(nextValue) => {
                            onChange(updateObjectField(value, fieldName, nextValue));
                        }}
                        onClear={() => {
                            onChange(removeObjectField(value, fieldName));
                        }}
                        showBorder={index > 0}
                    />
                );
            })}
        </div>
    );
}

function ObjectSettingField({
    fieldName,
    fieldPath,
    schema,
    value,
    hasValue,
    disabled,
    onChange,
    onClear,
    showBorder,
}: {
    fieldName: string;
    fieldPath: string;
    schema: Record<string, unknown>;
    value: unknown;
    hasValue: boolean;
    disabled: boolean;
    onChange: (value: unknown) => void;
    onClear: () => void;
    showBorder: boolean;
}) {
    const t = useT("editor");
    const resolveText = useSettingsTextResolver();
    const description =
        typeof schema.description === "string" ? schema.description : null;
    const defaultValue = Object.hasOwn(schema, "default")
        ? schema.default
        : undefined;

    return (
        <div
            className={cn("px-3 py-2.5", showBorder && "border-t border-border/70")}
        >
            <div className="flex flex-wrap items-center gap-1.5">
                <span
                    className="text-sm text-foreground"
                    title={description ?? undefined}
                >
                    {fieldName}
                </span>
                <MiniBadge>{schemaTypeLabel(schema)}</MiniBadge>
                {!hasValue ? <MiniBadge>{t("settings.value.inheritDefault")}</MiniBadge> : null}
                {defaultValue !== undefined ? (
                    <MiniBadge>
                        {t("settings.value.defaultValue", {
                            value: summarizeListValue(defaultValue, resolveText),
                        })}
                    </MiniBadge>
                ) : null}
                {hasValue && !disabled ? (
                    <button
                        type="button"
                        onClick={onClear}
                        className="rounded-md border border-border px-2 py-0.5 text-[11px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    >
                        {t("settings.clear")}
                    </button>
                ) : null}
            </div>
            {description ? (
                <div className="mt-1 text-xs leading-5 text-muted-foreground">
                    {description}
                </div>
            ) : null}
            <div className="mt-2 min-w-0">
                <SchemaValueEditor
                    fieldPath={fieldPath}
                    schema={schema}
                    value={value}
                    disabled={disabled}
                    onChange={onChange}
                />
            </div>
        </div>
    );
}

function SchemaValueEditor({
    fieldPath,
    schema,
    value,
    disabled,
    onChange,
}: {
    fieldPath: string;
    schema: Record<string, unknown>;
    value: unknown;
    disabled: boolean;
    onChange: (value: unknown) => void;
}) {
    const rawType = schemaPrimaryType(schema);

    if (rawType === "boolean") {
        return (
            <DraftBooleanSelect
                value={typeof value === "boolean" ? value : undefined}
                disabled={disabled}
                onChange={onChange}
            />
        );
    }

    if (
        rawType === "string" &&
        Array.isArray(schema.enum) &&
        schema.enum.every((item) => typeof item === "string")
    ) {
        return (
            <DraftEnumSelect
                value={typeof value === "string" ? value : undefined}
                options={schema.enum as string[]}
                disabled={disabled}
                onChange={onChange}
            />
        );
    }

    if (rawType === "string") {
        if (directoryLikeKey(fieldPath)) {
            return (
                <DirectoryDraftInput
                    value={typeof value === "string" ? value : ""}
                    disabled={disabled}
                    onChange={onChange}
                />
            );
        }
        return (
            <DraftTextInput
                type={schema.format === "secret" ? "password" : "text"}
                value={typeof value === "string" ? value : ""}
                disabled={disabled}
                onChange={(nextValue) => onChange(nextValue)}
            />
        );
    }

    if (rawType === "integer" || rawType === "number") {
        return (
            <DraftNumberInput
                value={typeof value === "number" ? value : undefined}
                integer={rawType === "integer"}
                disabled={disabled}
                onChange={onChange}
            />
        );
    }

    if (rawType === "array") {
        const itemType = schemaItemsPrimaryType(schema);
        if (itemType === "string" && directoryLikeKey(fieldPath)) {
            return (
                <DirectoryListEditor
                    value={Array.isArray(value) ? value : []}
                    disabled={disabled}
                    onChange={onChange}
                />
            );
        }
        if (itemType === "string") {
            return (
                <StringListEditor
                    value={Array.isArray(value) ? value : []}
                    disabled={disabled}
                    onChange={onChange}
                />
            );
        }
    }

    if (rawType === "object" && hasSchemaProperties(schema)) {
        return (
            <div className="rounded-md border border-border/70 bg-background/80">
                <ObjectSettingEditor
                    entry={{
                        key: fieldPath,
                        owner: "editor",
                        scope: "application",
                        schema,
                        defaultValue: {},
                        effect: "immediate",
                        targets: ["user"],
                        writableTargets: ["user"],
                        title: fieldPath,
                        description: "",
                    }}
                    value={isPlainObject(value) ? value : {}}
                    disabled={disabled}
                    onChange={onChange}
                />
            </div>
        );
    }

    return (
        <RawJsonDraftEditor value={value} disabled={disabled} onChange={onChange} />
    );
}

function DraftTextInput({
    type = "text",
    value,
    disabled,
    onChange,
}: {
    type?: "text" | "password";
    value: string;
    disabled: boolean;
    onChange: (value: string) => void;
}) {
    const [text, setText] = useState(value);

    useEffect(() => {
        setText(value);
    }, [value]);

    return (
        <input
            type={type}
            value={text}
            disabled={disabled}
            onChange={(event) => {
                setText(event.target.value);
                onChange(event.target.value);
            }}
            className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none transition-colors focus:border-primary"
        />
    );
}

function DirectoryDraftInput({
    value,
    disabled,
    onChange,
}: {
    value: string;
    disabled: boolean;
    onChange: (value: unknown) => void;
}) {
    const t = useT("editor");
    const [text, setText] = useState(value);

    useEffect(() => {
        setText(value);
    }, [value]);

    const handlePickFolder = async () => {
        const selected = await showFolderPickerDialog({
            title: t("settings.directory.pick"),
            initialPath: text || undefined,
        });
        if (!selected) return;
        setText(selected);
        onChange(selected);
    };

    return (
        <div className="flex gap-2">
            <input
                type="text"
                value={text}
                disabled={disabled}
                onChange={(event) => {
                    setText(event.target.value);
                    onChange(event.target.value);
                }}
                className="h-9 min-w-0 flex-1 rounded-md border border-border bg-background px-3 text-sm outline-none transition-colors focus:border-primary"
            />
            <button
                type="button"
                disabled={disabled}
                onClick={() => {
                    void handlePickFolder();
                }}
                className={cn(
                    "inline-flex h-9 shrink-0 items-center gap-1 rounded-md border px-3 text-sm transition-colors",
                    disabled
                        ? "border-border/60 text-muted-foreground/50"
                        : "border-border text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
            >
                <FolderOpen size={14} />
                {t("settings.directory.pick")}
            </button>
        </div>
    );
}

function DraftNumberInput({
    value,
    integer,
    disabled,
    onChange,
}: {
    value: number | undefined;
    integer: boolean;
    disabled: boolean;
    onChange: (value: unknown) => void;
}) {
    const [text, setText] = useState(value === undefined ? "" : String(value));

    useEffect(() => {
        setText(value === undefined ? "" : String(value));
    }, [value]);

    return (
        <input
            type="text"
            inputMode="numeric"
            value={text}
            disabled={disabled}
            onChange={(event) => {
                const nextText = event.target.value;
                setText(nextText);
                if (nextText.trim() === "") {
                    onChange(undefined);
                    return;
                }
                const parsed = Number(nextText);
                if (!Number.isFinite(parsed)) return;
                if (integer && !Number.isInteger(parsed)) return;
                onChange(parsed);
            }}
            onBlur={() => {
                setText(value === undefined ? "" : String(value));
            }}
            className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none transition-colors focus:border-primary"
        />
    );
}

function DraftBooleanSelect({
    value,
    disabled,
    onChange,
}: {
    value: boolean | undefined;
    disabled: boolean;
    onChange: (value: unknown) => void;
}) {
    const t = useT("editor");
    return (
        <select
            value={value === undefined ? "" : value ? "true" : "false"}
            disabled={disabled}
            onChange={(event) => {
                if (event.target.value === "") {
                    onChange(undefined);
                    return;
                }
                onChange(event.target.value === "true");
            }}
            className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none transition-colors focus:border-primary"
        >
            <option value="">{t("settings.value.useDefault")}</option>
            <option value="true">{t("settings.boolean.on")}</option>
            <option value="false">{t("settings.boolean.off")}</option>
        </select>
    );
}

function DraftEnumSelect({
    value,
    options,
    disabled,
    onChange,
}: {
    value: string | undefined;
    options: string[];
    disabled: boolean;
    onChange: (value: unknown) => void;
}) {
    const t = useT("editor");
    return (
        <select
            value={value ?? ""}
            disabled={disabled}
            onChange={(event) => {
                if (event.target.value === "") {
                    onChange(undefined);
                    return;
                }
                onChange(event.target.value);
            }}
            className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none transition-colors focus:border-primary"
        >
            <option value="">{t("settings.value.useDefault")}</option>
            {options.map((item) => (
                <option key={item} value={item}>
                    {item}
                </option>
            ))}
        </select>
    );
}

function StringListEditor({
    value,
    disabled,
    onChange,
}: {
    value: unknown[];
    disabled: boolean;
    onChange: (value: unknown) => void;
}) {
    const entries = value.filter(
        (item): item is string => typeof item === "string",
    );
    const [text, setText] = useState(entries.join("\n"));

    useEffect(() => {
        setText(entries.join("\n"));
    }, [entries]);

    return (
        <textarea
            value={text}
            disabled={disabled}
            wrap="soft"
            spellCheck={false}
            onChange={(event) => {
                setText(event.target.value);
                onChange(parseStringList(event.target.value));
            }}
            className="min-h-24 w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-sm leading-5 outline-none transition-colors focus:border-primary"
        />
    );
}

function CloudSourcesEditor({
    value,
    disabled,
    onChange,
}: {
    value: unknown[];
    disabled: boolean;
    onChange: (value: unknown) => void;
}) {
    const entriesText = value
        .map(formatCloudSourceLine)
        .filter(Boolean)
        .join("\n");
    const [text, setText] = useState(entriesText);

    useEffect(() => {
        setText(entriesText);
    }, [entriesText]);

    return (
        <textarea
            value={text}
            disabled={disabled}
            wrap="soft"
            spellCheck={false}
            onChange={(event) => {
                const nextText = event.target.value;
                setText(nextText);
                onChange(parseCloudSourcesEditorText(nextText));
            }}
            className="min-h-28 w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-sm leading-5 outline-none transition-colors focus:border-primary"
        />
    );
}

function formatCloudSourceLine(value: unknown): string {
    if (typeof value === "string") return value;
    if (isPlainObject(value)) return JSON.stringify(value);
    return "";
}

function parseCloudSourcesEditorText(text: string): unknown[] {
    return text
        .split(/\n/)
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
            if (line.startsWith("{") && line.endsWith("}")) {
                try {
                    const parsed = JSON.parse(line) as unknown;
                    if (isPlainObject(parsed)) return parsed;
                } catch {
                    return line;
                }
            }
            return line;
        });
}

function RawJsonDraftEditor({
    value,
    disabled,
    onChange,
}: {
    value: unknown;
    disabled: boolean;
    onChange: (value: unknown) => void;
}) {
    const [text, setText] = useState(formatRawJsonValue(value));

    useEffect(() => {
        setText(formatRawJsonValue(value));
    }, [value]);

    return (
        <textarea
            value={text}
            disabled={disabled}
            spellCheck={false}
            onChange={(event) => {
                const nextText = event.target.value;
                setText(nextText);
                try {
                    onChange(JSON.parse(nextText));
                } catch {
                    // Preserve local draft text until it becomes valid JSON.
                }
            }}
            className="min-h-28 w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-sm leading-5 outline-none transition-colors focus:border-primary"
        />
    );
}

function DirectoryCommitInput({
    value,
    disabled,
    onChange,
    onCommit,
    onDiscard,
}: {
    value: string;
    disabled: boolean;
    onChange: (value: unknown) => void;
    onCommit: (value: unknown) => Promise<void>;
    onDiscard: () => void;
}) {
    const t = useT("editor");
    const [text, setText] = useState(value);
    const dirtyRef = useRef(false);

    useEffect(() => {
        if (!dirtyRef.current) {
            setText(value);
        }
    }, [value]);

    const commit = async (nextValue = text) => {
        if (!dirtyRef.current && nextValue === text) {
            onDiscard();
            return;
        }
        dirtyRef.current = false;
        onChange(nextValue);
        await onCommit(nextValue);
    };

    const handleKeyDown = async (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            event.preventDefault();
            await commit();
        }
        if (event.key === "Escape") {
            event.preventDefault();
            dirtyRef.current = false;
            setText(value);
            onDiscard();
        }
    };

    const handlePickFolder = async () => {
        const selected = await showFolderPickerDialog({
            title: t("settings.directory.pick"),
            initialPath: text || undefined,
        });
        if (!selected) return;
        dirtyRef.current = false;
        setText(selected);
        onChange(selected);
        await onCommit(selected);
    };

    return (
        <div className="flex gap-2">
            <input
                type="text"
                value={text}
                disabled={disabled}
                onChange={(event) => {
                    dirtyRef.current = true;
                    setText(event.target.value);
                }}
                onBlur={() => {
                    void commit();
                }}
                onKeyDown={(event) => {
                    void handleKeyDown(event);
                }}
                className="h-9 min-w-0 flex-1 rounded-md border border-border bg-background px-3 text-sm outline-none transition-colors focus:border-primary"
            />
            <button
                type="button"
                disabled={disabled}
                onClick={() => {
                    void handlePickFolder();
                }}
                className={cn(
                    "inline-flex h-9 shrink-0 items-center gap-1 rounded-md border px-3 text-sm transition-colors",
                    disabled
                        ? "border-border/60 text-muted-foreground/50"
                        : "border-border text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
            >
                <FolderOpen size={14} />
                {t("settings.directory.pick")}
            </button>
        </div>
    );
}

function DirectoryListEditor({
    value,
    disabled,
    onChange,
}: {
    value: unknown[];
    disabled: boolean;
    onChange: (value: unknown) => void;
}) {
    const t = useT("editor");
    const entries = value.filter(
        (item): item is string => typeof item === "string",
    );
    const entriesText = entries.join("\n");
    const [text, setText] = useState(entriesText);
    const parsedEntries = parseStringList(text);

    useEffect(() => {
        setText(entriesText);
    }, [entriesText]);

    const updateEntries = (nextText: string) => {
        setText(nextText);
        onChange(parseStringList(nextText));
    };

    const handlePickFolder = async () => {
        const initialPath =
            parsedEntries.length > 0
                ? parsedEntries[parsedEntries.length - 1]
                : undefined;
        const selected = await showFolderPickerDialog({
            title: t("settings.directory.add"),
            initialPath,
        });
        if (!selected) return;
        if (parsedEntries.includes(selected)) return;
        updateEntries([...parsedEntries, selected].join("\n"));
    };

    return (
        <div className="space-y-3">
            <textarea
                value={text}
                disabled={disabled}
                wrap="soft"
                spellCheck={false}
                onChange={(event) => updateEntries(event.target.value)}
                className="min-h-28 w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-sm leading-5 outline-none transition-colors focus:border-primary"
            />
            <div className="rounded-md border border-border/80 bg-muted/20 px-3 py-2">
                {parsedEntries.length > 0 ? (
                    <div className="space-y-2">
                        {parsedEntries.map((entry) => (
                            <div
                                key={entry}
                                className="flex items-start justify-between gap-3 text-sm"
                            >
                                <div className="min-w-0 flex-1 break-all font-mono text-foreground">
                                    {entry}
                                </div>
                                {!disabled ? (
                                    <button
                                        type="button"
                                        onClick={() =>
                                            updateEntries(
                                                parsedEntries
                                                    .filter((item) => item !== entry)
                                                    .join("\n"),
                                            )
                                        }
                                        className="shrink-0 text-xs text-muted-foreground transition-colors hover:text-foreground"
                                    >
                                        {t("settings.remove")}
                                    </button>
                                ) : null}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-sm text-muted-foreground">
                        {t("settings.directory.emptyHelp")}
                    </div>
                )}
            </div>
            <div className="flex items-center justify-between gap-3">
                <div className="text-xs text-muted-foreground">
                    {t("settings.directory.lineHelp")}
                </div>
                <button
                    type="button"
                    disabled={disabled}
                    onClick={() => {
                        void handlePickFolder();
                    }}
                    className={cn(
                        "inline-flex h-9 shrink-0 items-center gap-1 rounded-md border px-3 text-sm transition-colors",
                        disabled
                            ? "border-border/60 text-muted-foreground/50"
                            : "border-border text-muted-foreground hover:bg-muted hover:text-foreground",
                    )}
                >
                    <FolderOpen size={14} />
                    {t("settings.directory.add")}
                </button>
            </div>
        </div>
    );
}

function CommitInput({
    type,
    value,
    disabled,
    onChange,
    onCommit,
    onDiscard,
}: {
    type: "text" | "number";
    value: string;
    disabled: boolean;
    onChange: (value: unknown) => void;
    onCommit: (value: unknown) => Promise<void>;
    onDiscard: () => void;
}) {
    const [text, setText] = useState(value);
    const dirtyRef = useRef(false);

    useEffect(() => {
        if (!dirtyRef.current) {
            setText(value);
        }
    }, [value]);

    const commit = async () => {
        if (!dirtyRef.current) {
            onDiscard();
            return;
        }
        dirtyRef.current = false;
        onChange(text);
        await onCommit(text);
    };

    const handleKeyDown = async (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            event.preventDefault();
            await commit();
        }
        if (event.key === "Escape") {
            event.preventDefault();
            dirtyRef.current = false;
            setText(value);
            onDiscard();
        }
    };

    return (
        <input
            type={type}
            value={text}
            disabled={disabled}
            onChange={(event) => {
                dirtyRef.current = true;
                setText(event.target.value);
            }}
            onBlur={() => {
                void commit();
            }}
            onKeyDown={(event) => {
                void handleKeyDown(event);
            }}
            className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none transition-colors focus:border-primary"
        />
    );
}
